import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  titleWithVersion,
  type OrgMember,
  type PolicyAcceptanceStatus,
  type PolicyReleaseSummary,
  type RecordPolicyAcceptanceInput,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Button, Dialog, Field, Input, Select, useToast } from '../../components/ui.js';
import { policiesKey, todayIso } from './shared.js';

const DEFAULT_SOURCE = 'Signed paper form';

/**
 * Record an acceptance given outside the app (a paper form, a previous
 * platform) for a current member. Recorded against the live release and
 * marked as recorded wherever status is shown — never confused with an
 * in-app acceptance.
 */
export function RecordAcceptanceDialog({
  policies,
  open,
  onClose,
}: {
  policies: PolicyReleaseSummary[];
  open: boolean;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: open,
  });

  const [userId, setUserId] = useState('');
  const [corpusItemId, setCorpusItemId] = useState('');
  const [acceptedOn, setAcceptedOn] = useState(todayIso());
  const [source, setSource] = useState(DEFAULT_SOURCE);

  const record = useMutation({
    mutationFn: () =>
      apiFetch<PolicyAcceptanceStatus>(`/orgs/${org.orgId}/policy-acceptance/records`, {
        method: 'POST',
        body: {
          userId,
          corpusItemId,
          acceptedOn,
          source: source.trim(),
        } satisfies RecordPolicyAcceptanceInput,
      }),
    onSuccess: () => {
      toast('Acceptance recorded.', 'success');
      void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
      onClose();
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not record the acceptance', 'error'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!userId || !corpusItemId) return;
    record.mutate();
  };

  const active = (members.data ?? []).filter((m) => m.status === 'active');

  return (
    <Dialog open={open} onClose={onClose} title="Record acceptance">
      <div className="policy-dialog">
        <p className="policy-dialog-intro">
          Record that someone accepted the current version of a policy outside the app — for
          example on a signed form or a previous platform. It is shown as recorded, with your name
          and the source, wherever acceptance is tracked.
        </p>
        <form onSubmit={submit}>
          <Field label="Member" required>
            {(props) => (
              <Select {...props} required value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Select a member…</option>
                {active.map((m) => (
                  <option key={m.user.id} value={m.user.id}>
                    {m.user.name} ({m.user.email})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Policy" required hint="Only policies currently published for acceptance">
            {(props) => (
              <Select
                {...props}
                required
                value={corpusItemId}
                onChange={(e) => setCorpusItemId(e.target.value)}
              >
                <option value="">Select a policy…</option>
                {policies.map((p) => (
                  <option key={p.releaseId} value={p.corpusItemId}>
                    {titleWithVersion(p.title, p.documentVersion, p.versionNumber)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Accepted on" required>
            {(props) => (
              <Input
                {...props}
                type="date"
                required
                max={todayIso()}
                value={acceptedOn}
                onChange={(e) => setAcceptedOn(e.target.value)}
              />
            )}
          </Field>
          <Field label="Source" required hint="Where the acceptance was given, e.g. the signed form or previous platform">
            {(props) => (
              <Input
                {...props}
                required
                maxLength={500}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            )}
          </Field>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!userId || !corpusItemId || !acceptedOn || source.trim() === ''}
              loading={record.isPending}
            >
              Record acceptance
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
